<template>
  <div class="element-config">
    <div v-if="!selectedElement" class="no-selection">
      <div class="no-selection-icon">🔧</div>
      <div class="no-selection-text">请选择一个元素来编辑其属性</div>
    </div>
    
    <div v-else>
      <ConfigSection :title="getElementTitle(selectedElement)" icon="🔧">
        <div class="element-info">
          <div class="info-item">
            <span class="info-label">类型:</span>
            <span class="info-value">{{ getElementType(selectedElement) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">ID:</span>
            <span class="info-value">{{ selectedElement.id || '未设置' }}</span>
          </div>
        </div>
        
        <!-- 位置和尺寸 -->
        <div class="form-group">
          <label class="form-label">位置和尺寸</label>
          <div class="position-grid">
            <div class="form-field">
              <label>X (px)</label>
              <input
                v-model.number="elementX"
                type="number"
                class="form-control"
                min="0"
              />
            </div>
            <div class="form-field">
              <label>Y (px)</label>
              <input
                v-model.number="elementY"
                type="number"
                class="form-control"
                min="0"
              />
            </div>
            <div class="form-field">
              <label>宽度 (px)</label>
              <input
                v-model.number="elementWidth"
                type="number"
                class="form-control"
                min="10"
              />
            </div>
            <div class="form-field">
              <label>高度 (px)</label>
              <input
                v-model.number="elementHeight"
                type="number"
                class="form-control"
                min="10"
              />
            </div>
          </div>
        </div>
        
        <!-- 文本元素特有属性 -->
        <template v-if="selectedElement.type === 'text'">
          <div class="form-group">
            <label class="form-label">文本内容</label>
            <textarea
              v-model="textContent"
              class="form-control"
              rows="3"
              placeholder="请输入文本内容"
            ></textarea>
          </div>
          
          <div class="form-group">
            <label class="form-label">字体设置</label>
            <div class="font-controls">
              <select v-model="textFontFamily" class="form-control">
                <option value="微软雅黑">微软雅黑</option>
                <option value="宋体">宋体</option>
                <option value="黑体">黑体</option>
                <option value="楷体">楷体</option>
              </select>
              <input
                v-model.number="textFontSize"
                type="number"
                class="form-control"
                min="8"
                max="72"
                placeholder="字号"
              />
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label">文本样式</label>
            <div class="style-controls">
              <label class="checkbox-item">
                <input v-model="textBold" type="checkbox" />
                <span class="checkbox-label">粗体</span>
              </label>
              <label class="checkbox-item">
                <input v-model="textItalic" type="checkbox" />
                <span class="checkbox-label">斜体</span>
              </label>
              <label class="checkbox-item">
                <input v-model="textUnderline" type="checkbox" />
                <span class="checkbox-label">下划线</span>
              </label>
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label">文本颜色</label>
            <div class="color-picker">
              <input
                v-model="textColor"
                type="color"
                class="color-input"
              />
              <input
                v-model="textColor"
                type="text"
                class="form-control"
                placeholder="#000000"
              />
            </div>
          </div>
        </template>
        
        <!-- 表格元素特有属性 -->
        <template v-if="selectedElement.type === 'table'">
          <div class="form-group">
            <label class="form-label">表格设置</label>
            <div class="table-controls">
              <div class="form-field">
                <label>行高 (px)</label>
                <input
                  v-model.number="tableRowHeight"
                  type="number"
                  class="form-control"
                  min="20"
                  max="100"
                />
              </div>
              <div class="form-field">
                <label>列数</label>
                <input
                  :value="selectedElement.columns?.length || 0"
                  type="number"
                  class="form-control"
                  readonly
                />
              </div>
            </div>
          </div>
          
          <div v-if="selectedElement.columns" class="form-group">
            <label class="form-label">列设置 (可拖拽排序)</label>
            <div class="columns-list" ref="columnsList">
              <div
                v-for="(column, index) in selectedElement.columns"
                :key="column.id"
                class="column-item"
                draggable="true"
                @dragstart="handleDragStart(index, $event)"
                @dragover.prevent
                @drop="handleDrop(index, $event)"
                @dragend="handleDragEnd"
              >
                <div class="column-header">
                  <span class="drag-handle">⋮⋮</span>
                  <span class="column-title">{{ column.title }}</span>
                  <button
                    @click="removeColumn(index)"
                    class="remove-btn"
                    title="删除列"
                  >
                    ✕
                  </button>
                </div>
                <div class="column-controls">
                  <div class="form-field">
                    <label>标题</label>
                    <input
                      :value="getColumnTitle(index)"
                      @input="updateColumnTitle(index, $event.target.value)"
                      type="text"
                      class="form-control"
                      placeholder="列标题"
                    />
                  </div>
                  <div class="form-field">
                    <label>宽度</label>
                    <input
                      :value="getColumnWidth(index)"
                      @input="updateColumnWidth(index, $event.target.value)"
                      type="number"
                      class="form-control"
                      min="50"
                      placeholder="宽度"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        
        <!-- 二维码元素特有属性 -->
        <template v-if="selectedElement.type === 'qrcode'">
          <div class="form-group">
            <label class="form-label">二维码内容</label>
            <textarea
              v-model="qrCodeContent"
              class="form-control"
              rows="3"
              placeholder="请输入二维码内容（网址、文本等）"
            ></textarea>
          </div>
          
          <div class="form-group">
            <label class="form-label">二维码尺寸</label>
            <div class="qrcode-size-controls">
              <div class="form-field">
                <label>宽度 (px)</label>
                <input
                  v-model.number="qrCodeWidth"
                  type="number"
                  class="form-control"
                  min="50"
                  max="300"
                />
              </div>
              <div class="form-field">
                <label>高度 (px)</label>
                <input
                  v-model.number="qrCodeHeight"
                  type="number"
                  class="form-control"
                  min="50"
                  max="300"
                />
              </div>
            </div>
          </div>
        </template>
        
        <!-- 条形码元素特有属性 -->
        <template v-if="selectedElement.type === 'barcode'">
          <div class="form-group">
            <label class="form-label">条形码内容</label>
            <input
              v-model="barcodeContent"
              type="text"
              class="form-control"
              placeholder="请输入条形码内容（数字）"
            />
          </div>
          
          <div class="form-group">
            <label class="form-label">条形码尺寸</label>
            <div class="qrcode-size-controls">
              <div class="form-field">
                <label>宽度 (px)</label>
                <input
                  v-model.number="barcodeWidth"
                  type="number"
                  class="form-control"
                  min="80"
                  max="400"
                />
              </div>
              <div class="form-field">
                <label>高度 (px)</label>
                <input
                  v-model.number="barcodeHeight"
                  type="number"
                  class="form-control"
                  min="30"
                  max="100"
                />
              </div>
            </div>
          </div>
        </template>
        
        <!-- 通用样式 -->
        <div class="form-group">
          <label class="form-label">边框和背景</label>
          <div class="border-bg-controls">
            <div class="form-field">
              <label>边框宽度</label>
              <input
                v-model.number="borderWidth"
                type="number"
                class="form-control"
                min="0"
                max="10"
              />
            </div>
            <div class="form-field">
              <label>边框颜色</label>
              <input
                v-model="borderColor"
                type="color"
                class="color-input"
              />
            </div>
            <div class="form-field">
              <label>背景色</label>
              <input
                v-model="backgroundColor"
                type="color"
                class="color-input"
              />
            </div>
          </div>
        </div>
        
        <!-- 操作按钮 -->
        <div class="actions">
          <button @click="duplicateElement" class="action-btn duplicate-btn">
            📋 复制元素
          </button>
          <button @click="deleteElement" class="action-btn delete-btn">
            🗑️ 删除元素
          </button>
        </div>
      </ConfigSection>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import ConfigSection from './ConfigSection.vue'

export default {
  name: 'ElementConfig',
  components: {
    ConfigSection
  },
  
  setup() {
    const store = useStore()
    
    const selectedElement = computed(() => store.getters['canvas/selectedElement'])
    const selectedElementIndex = computed(() => store.state.canvas.selectedElementIndex)
    
    // 位置和尺寸
    const elementX = computed({
      get: () => {
        if (!selectedElement.value) return 0
        return parseInt(selectedElement.value.style.left) || 0
      },
      set: (value) => updateElementStyle('left', value + 'px')
    })
    
    const elementY = computed({
      get: () => {
        if (!selectedElement.value) return 0
        return parseInt(selectedElement.value.style.top) || 0
      },
      set: (value) => updateElementStyle('top', value + 'px')
    })
    
    const elementWidth = computed({
      get: () => {
        if (!selectedElement.value) return 100
        return parseInt(selectedElement.value.style.width) || 100
      },
      set: (value) => updateElementStyle('width', value + 'px')
    })
    
    const elementHeight = computed({
      get: () => {
        if (!selectedElement.value) return 30
        return parseInt(selectedElement.value.style.height) || 30
      },
      set: (value) => updateElementStyle('height', value + 'px')
    })
    
    // 文本属性
    const textContent = computed({
      get: () => selectedElement.value?.content || '',
      set: (value) => updateElement({ content: value })
    })
    
    const textFontFamily = computed({
      get: () => selectedElement.value?.style?.fontFamily || '微软雅黑',
      set: (value) => updateElementStyle('fontFamily', value)
    })
    
    const textFontSize = computed({
      get: () => parseInt(selectedElement.value?.style?.fontSize) || 14,
      set: (value) => updateElementStyle('fontSize', value + 'px')
    })
    
    const textBold = computed({
      get: () => selectedElement.value?.style?.fontWeight === 'bold',
      set: (value) => updateElementStyle('fontWeight', value ? 'bold' : 'normal')
    })
    
    const textItalic = computed({
      get: () => selectedElement.value?.style?.fontStyle === 'italic',
      set: (value) => updateElementStyle('fontStyle', value ? 'italic' : 'normal')
    })
    
    const textUnderline = computed({
      get: () => selectedElement.value?.style?.textDecoration === 'underline',
      set: (value) => updateElementStyle('textDecoration', value ? 'underline' : 'none')
    })
    
    const textColor = computed({
      get: () => selectedElement.value?.style?.color || '#000000',
      set: (value) => updateElementStyle('color', value)
    })
    
    // 表格属性
    const tableRowHeight = computed({
      get: () => selectedElement.value?.rowHeight || 32,
      set: (value) => updateElement({ rowHeight: value })
    })
    
    // 二维码属性
    const qrCodeContent = computed({
      get: () => selectedElement.value?.content || '',
      set: (value) => updateElement({ content: value })
    })
    
    const qrCodeWidth = computed({
      get: () => {
        if (!selectedElement.value) return 80
        return parseInt(selectedElement.value.style.width) || 80
      },
      set: (value) => updateElementStyle('width', value + 'px')
    })
    
    const qrCodeHeight = computed({
      get: () => {
        if (!selectedElement.value) return 80
        return parseInt(selectedElement.value.style.height) || 80
      },
      set: (value) => updateElementStyle('height', value + 'px')
    })
    
    // 条形码属性
    const barcodeContent = computed({
      get: () => selectedElement.value?.content || '',
      set: (value) => updateElement({ content: value })
    })
    
    const barcodeWidth = computed({
      get: () => {
        if (!selectedElement.value) return 120
        return parseInt(selectedElement.value.style.width) || 120
      },
      set: (value) => updateElementStyle('width', value + 'px')
    })
    
    const barcodeHeight = computed({
      get: () => {
        if (!selectedElement.value) return 40
        return parseInt(selectedElement.value.style.height) || 40
      },
      set: (value) => updateElementStyle('height', value + 'px')
    })
    
    // 通用样式
    const borderWidth = computed({
      get: () => {
        const border = selectedElement.value?.style?.border || '1px solid #000'
        return parseInt(border) || 1
      },
      set: (value) => {
        const color = borderColor.value
        updateElementStyle('border', `${value}px solid ${color}`)
      }
    })
    
    const borderColor = computed({
      get: () => {
        const border = selectedElement.value?.style?.border || '1px solid #000000'
        const match = border.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/)
        return match ? match[0] : '#000000'
      },
      set: (value) => {
        const width = borderWidth.value
        updateElementStyle('border', `${width}px solid ${value}`)
      }
    })
    
    const backgroundColor = computed({
      get: () => selectedElement.value?.style?.backgroundColor || '#ffffff',
      set: (value) => updateElementStyle('backgroundColor', value)
    })
    
    // 方法
    const updateElement = (updates) => {
      if (selectedElementIndex.value === null) return
      
      const updatedElement = {
        ...selectedElement.value,
        ...updates
      }
      
      store.dispatch('canvas/updateElement', {
        index: selectedElementIndex.value,
        element: updatedElement
      })
    }
    
    const updateElementStyle = (property, value) => {
      if (selectedElementIndex.value === null) return
      
      const updatedElement = {
        ...selectedElement.value,
        style: {
          ...selectedElement.value.style,
          [property]: value
        }
      }
      
      store.dispatch('canvas/updateElement', {
        index: selectedElementIndex.value,
        element: updatedElement
      })
    }
    
    const getElementTitle = (element) => {
      if (element.type === 'text') {
        return `文本元素: ${element.content?.substring(0, 20) || '未命名'}${element.content?.length > 20 ? '...' : ''}`
      } else if (element.type === 'table') {
        return `表格元素 (${element.columns?.length || 0}列)`
      }
      return '元素属性'
    }
    
    const getElementType = (element) => {
      const typeMap = {
        text: '文本',
        table: '表格',
        image: '图片',
        barcode: '条码',
        qrcode: '二维码',
        line: '线段'
      }
      return typeMap[element.type] || '未知'
    }
    
    const removeColumn = (columnIndex) => {
      if (!selectedElement.value || selectedElement.value.type !== 'table') return
      
      const updatedColumns = [...selectedElement.value.columns]
      updatedColumns.splice(columnIndex, 1)
      
      updateElement({
        columns: updatedColumns,
        data: generateMockTableData(updatedColumns)
      })
    }
    
    // 拖拽排序相关
    let dragStartIndex = null
    
    const handleDragStart = (index, event) => {
      dragStartIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.target.classList.add('dragging')
    }
    
    const handleDrop = (dropIndex, event) => {
      event.preventDefault()
      if (dragStartIndex === null || dragStartIndex === dropIndex) return
      
      const updatedColumns = [...selectedElement.value.columns]
      const draggedColumn = updatedColumns[dragStartIndex]
      
      // 移除拖拽的列
      updatedColumns.splice(dragStartIndex, 1)
      
      // 插入到新位置
      updatedColumns.splice(dropIndex, 0, draggedColumn)
      
      updateElement({
        columns: updatedColumns,
        data: generateMockTableData(updatedColumns)
      })
      
      dragStartIndex = null
    }
    
    const handleDragEnd = (event) => {
      event.target.classList.remove('dragging')
      dragStartIndex = null
    }
    
    // 防抖处理
    let updateTimer = null
    
    const debounceUpdate = (callback, delay = 300) => {
      if (updateTimer) {
        clearTimeout(updateTimer)
      }
      updateTimer = setTimeout(callback, delay)
    }
    
    // 列属性获取
    const getColumnTitle = (index) => {
      if (!selectedElement.value || !selectedElement.value.columns) return ''
      return selectedElement.value.columns[index]?.title || ''
    }
    
    const getColumnWidth = (index) => {
      if (!selectedElement.value || !selectedElement.value.columns) return 50
      return selectedElement.value.columns[index]?.width || 50
    }
    
    // 列属性更新
    const updateColumnTitle = (index, title) => {
      if (!selectedElement.value || selectedElement.value.type !== 'table') return
      
      debounceUpdate(() => {
        const updatedColumns = [...selectedElement.value.columns]
        updatedColumns[index] = { ...updatedColumns[index], title }
        
        updateElement({
          columns: updatedColumns,
          data: generateMockTableData(updatedColumns)
        })
      })
    }
    
    const updateColumnWidth = (index, width) => {
      if (!selectedElement.value || selectedElement.value.type !== 'table') return
      
      debounceUpdate(() => {
        const updatedColumns = [...selectedElement.value.columns]
        updatedColumns[index] = { ...updatedColumns[index], width: parseInt(width) || 50 }
        
        updateElement({
          columns: updatedColumns,
          data: generateMockTableData(updatedColumns)
        })
      })
    }
    
    const generateMockTableData = (columns) => {
      // 简化的模拟数据生成
      return Array.from({ length: 3 }, (_, rowIndex) => {
        const row = {}
        columns.forEach(col => {
          row[col.id] = `示例${rowIndex + 1}`
        })
        return row
      })
    }
    
    const duplicateElement = () => {
      if (!selectedElement.value) return
      
      const duplicated = {
        ...selectedElement.value,
        id: `${selectedElement.value.id}_copy_${Date.now()}`,
        style: {
          ...selectedElement.value.style,
          left: (parseInt(selectedElement.value.style.left) + 20) + 'px',
          top: (parseInt(selectedElement.value.style.top) + 20) + 'px'
        }
      }
      
      store.dispatch('canvas/addElement', duplicated)
    }
    
    const deleteElement = () => {
      if (selectedElementIndex.value !== null) {
        if (confirm('确定要删除这个元素吗？')) {
          store.dispatch('canvas/removeElement', selectedElementIndex.value)
        }
      }
    }
    
    return {
      selectedElement,
      elementX,
      elementY,
      elementWidth,
      elementHeight,
      textContent,
      textFontFamily,
      textFontSize,
      textBold,
      textItalic,
      textUnderline,
      textColor,
      tableRowHeight,
      qrCodeContent,
      qrCodeWidth,
      qrCodeHeight,
      barcodeContent,
      barcodeWidth,
      barcodeHeight,
      borderWidth,
      borderColor,
      backgroundColor,
      getElementTitle,
      getElementType,
      getColumnTitle,
      getColumnWidth,
      removeColumn,
      handleDragStart,
      handleDrop,
      handleDragEnd,
      updateColumnTitle,
      updateColumnWidth,
      duplicateElement,
      deleteElement
    }
  }
}
</script>

<style scoped>
.element-config {
  height: 100%;
}

.no-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
  color: #6c757d;
}

.no-selection-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.no-selection-text {
  font-size: 16px;
}

.element-info {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 20px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  font-weight: 500;
  color: #495057;
}

.info-value {
  color: #6c757d;
  font-family: monospace;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #495057;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.position-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-field label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
}

.font-controls {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 8px;
}

.style-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-item input[type="checkbox"] {
  margin-right: 8px;
}

.checkbox-label {
  font-size: 14px;
  color: #495057;
}

.color-picker {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-input {
  width: 50px;
  height: 40px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  cursor: pointer;
  background: none;
}

.table-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.columns-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 4px;
}

.column-item {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
  cursor: grab;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.column-item:hover {
  background: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.column-item.dragging {
  opacity: 0.5;
  transform: rotate(5deg);
  cursor: grabbing;
}

.column-item:active {
  cursor: grabbing;
}

.column-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.drag-handle {
  color: #6c757d;
  font-size: 16px;
  cursor: grab;
  user-select: none;
  padding: 2px;
  border-radius: 3px;
  transition: color 0.2s;
}

.drag-handle:hover {
  color: #495057;
  background: rgba(0,0,0,0.05);
}

.column-title {
  font-weight: 500;
  color: #495057;
}

.remove-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn:hover {
  background: #c82333;
}

.column-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
}

.column-controls .form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.column-controls .form-field label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
}

.qrcode-size-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.qrcode-size-controls .form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.qrcode-size-controls .form-field label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
}

.border-bg-controls {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
}

.actions {
  display: flex;
  gap: 8px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.action-btn {
  flex: 1;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.duplicate-btn {
  background: #28a745;
  color: white;
}

.duplicate-btn:hover {
  background: #218838;
}

.delete-btn {
  background: #dc3545;
  color: white;
}

.delete-btn:hover {
  background: #c82333;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .position-grid {
    grid-template-columns: 1fr;
  }
  
  .font-controls {
    grid-template-columns: 1fr;
  }
  
  .table-controls {
    grid-template-columns: 1fr;
  }
  
  .column-controls {
    grid-template-columns: 1fr;
  }
  
  .border-bg-controls {
    grid-template-columns: 1fr;
  }
  
  .actions {
    flex-direction: column;
  }
}
</style>
